import { useRef, useState, useEffect } from 'react';
import Hls from 'hls.js';
import { Maximize2, RefreshCw, VideoOff } from 'lucide-react';
import styles from './VideoPlayer.module.css';

interface VideoPlayerProps {
  streamUrl?: string;
  autoPlay?: boolean;
  showStatus?: boolean;
  status?: 'active' | 'inactive';
  onError?: (error: Error) => void;
  onLoad?: () => void;
}

export function VideoPlayer({
  streamUrl,
  autoPlay = true,
  showStatus = false,
  status = 'inactive',
  onError,
  onLoad
}: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Определяем тип потока: Flussonic MSE-LD использует iframe с embed.html
  const isMSE = streamUrl?.includes('.ll.m3u8') || 
                streamUrl?.includes('flussonic') ||
                (streamUrl?.includes('/') && !streamUrl?.includes('.m3u8'));

  useEffect(() => {
    if (!streamUrl) {
      setHasError(true);
      setErrorMsg('URL потока не указан');
      return;
    }

    setIsLoading(true);
    setHasError(false);
    setErrorMsg('');
  }, [streamUrl]);

  const handleRetry = () => {
    setIsLoading(true);
    setHasError(false);
    if (iframeRef.current) {
      // Релоад iframe
      iframeRef.current.src = iframeRef.current.src;
    } else if (containerRef.current?.querySelector('video')) {
      const video = containerRef.current.querySelector('video') as HTMLVideoElement;
      video?.load();
    }
  };

  const handleFullscreen = () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen();
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleIframeError = () => {
    setHasError(true);
    setErrorMsg('Ошибка загрузки плеера');
    setIsLoading(false);
    onError?.(new Error('MSE-LD player error'));
  };

  // Для Flussonic MSE-LD используем iframe с embed.html
  if (isMSE && streamUrl) {
    // Извлекаем путь к потоку для embed.html
    const embedUrl = streamUrl.includes('embed.html') 
      ? streamUrl 
      : `${streamUrl}/embed.html?autoplay=true&realtime=true`;

    return (
      <div ref={containerRef} className={styles.container}>
        <iframe
          ref={iframeRef}
          src={embedUrl}
          className={styles.iframe}
          allow="fullscreen; autoplay"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />

        {showStatus && (
          <div className={`${styles.statusBadge} ${status === 'active' ? styles.statusActive : styles.statusInactive}`}>
            <span className={styles.statusDot} />
            {status === 'active' ? 'Онлайн' : 'Офлайн'}
          </div>
        )}

        <button className={styles.fullscreenButton} onClick={handleFullscreen}>
          <Maximize2 size={16} />
        </button>

        {isLoading && (
          <div className={styles.overlay}>
            <div className={styles.loadingSpinner} />
            <span className={styles.loadingText}>Подключение к потоку...</span>
          </div>
        )}

        {hasError && (
          <div className={styles.overlay}>
            <VideoOff size={40} className={styles.errorIcon} />
            <span className={styles.errorText}>{errorMsg || 'Ошибка'}</span>
            <button className={styles.retryButton} onClick={handleRetry}>
              <RefreshCw size={16} />
              Переподключить
            </button>
          </div>
        )}
      </div>
    );
  }

  // Для HLS используем hls.js
  return (
    <HLSVideoPlayer
      streamUrl={streamUrl}
      autoPlay={autoPlay}
      showStatus={showStatus}
      status={status}
      onLoad={onLoad}
      onError={onError}
      isLoading={isLoading}
      hasError={hasError}
      errorMsg={errorMsg}
      setIsLoading={setIsLoading}
      setHasError={setHasError}
      setErrorMsg={setErrorMsg}
    />
  );
}

interface HLSVideoPlayerProps {
  streamUrl?: string;
  autoPlay?: boolean;
  showStatus?: boolean;
  status?: 'active' | 'inactive';
  onLoad?: () => void;
  onError?: (e: Error) => void;
  isLoading: boolean;
  hasError: boolean;
  errorMsg: string;
  setIsLoading: (v: boolean) => void;
  setHasError: (v: boolean) => void;
  setErrorMsg: (v: string) => void;
}

function HLSVideoPlayer({ 
  streamUrl, 
  autoPlay, 
  showStatus,
  status,
  onLoad, 
  onError,
  isLoading,
  hasError,
  errorMsg,
  setIsLoading,
  setHasError,
  setErrorMsg
}: HLSVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    if (!streamUrl || !videoRef.current) return;

    // Cleanup previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const video = videoRef.current;
    setIsLoading(true);
    setHasError(false);
    setErrorMsg('');

    const isHls = streamUrl.includes('.m3u8');
    
    if (isHls && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        maxBufferLength: 30,
      });
      hlsRef.current = hls;

      hls.loadSource(streamUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsLoading(false);
        if (autoPlay) {
          video.play().catch(() => {});
        }
        onLoad?.();
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          setHasError(true);
          setErrorMsg('Ошибка потока');
          onError?.(new Error('HLS error'));
          hls.destroy();
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      video.src = streamUrl;
      video.play().catch(() => {});
      setIsLoading(false);
      onLoad?.();
    } else {
      // Direct video source
      video.src = streamUrl;
      video.play().catch(() => {});
      setIsLoading(false);
      onLoad?.();
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [streamUrl, autoPlay, onError, onLoad, setIsLoading, setHasError, setErrorMsg]);

  const handleRetry = () => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  };

  const handleFullscreen = () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen();
    }
  };

  const handleVideoError = () => {
    setHasError(true);
    setIsLoading(false);
    setErrorMsg('Ошибка воспроизведения');
  };

  return (
    <div ref={containerRef} className={styles.container}>
      <video
        ref={videoRef}
        className={styles.video}
        playsInline
        onError={handleVideoError}
        onCanPlay={() => setIsLoading(false)}
      />

      {showStatus && (
        <div className={`${styles.statusBadge} ${status === 'active' ? styles.statusActive : styles.statusInactive}`}>
          <span className={styles.statusDot} />
          {status === 'active' ? 'Онлайн' : 'Офлайн'}
        </div>
      )}

      <button className={styles.fullscreenButton} onClick={handleFullscreen}>
        <Maximize2 size={16} />
      </button>

      {isLoading && !hasError && (
        <div className={styles.overlay}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>Загрузка...</span>
        </div>
      )}

      {hasError && (
        <div className={styles.overlay}>
          <VideoOff size={40} className={styles.errorIcon} />
          <span className={styles.errorText}>{errorMsg}</span>
          <button className={styles.retryButton} onClick={handleRetry}>
            <RefreshCw size={16} />
            Повторить
          </button>
        </div>
      )}
    </div>
  );
}
